import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import VatuForm from '../components/VatuForm';
import Breadcrumb from '../components/Breadcrumb';
import { useLanguage } from '../context/LanguageContext';
import { useTeluguYear } from '../context/TeluguYearContext';
import api from '../utils/api';

// Telugu 60-year cycle names (Shashtiabda Samvatsaras)
const teluguYearNames = [
  'ప్రభవ', 'విభవ', 'శుక్ల', 'ప్రమోదూత', 'ప్రజోత్పత్తి', 'ఆంగీరస', 'శ్రీముఖ', 'భావ', 'యువ', 'ధాత',
  'ఈశ్వర', 'బహుధాన్య', 'ప్రమాథి', 'విక్రమ', 'విషు', 'చిత్రభాను', 'స్వభాను', 'తారణ', 'పార్థివ', 'వ్యయ',
  'సర్వజిత్', 'సర్వధారి', 'విరోధి', 'వికృతి', 'ఖర', 'నందన', 'విజయ', 'జయ', 'మన్మథ', 'దుర్ముఖి',
  'హేవళంబి', 'విళంబి', 'విఖారి', 'శార్వరి', 'ప్లవ', 'శుభకృత్', 'శోభకృత్', 'క్రోధి', 'విశ్వావసు', 'పరాభవ',
  'ప్లవంగ', 'కీలక', 'సౌమ్య', 'సాధారణ', 'విరోధకృత్', 'పరిధావి', 'ప్రమాదిచ', 'ఆనంద', 'రాక్షస', 'అనల',
  'పింగళ', 'కాళయుక్తి', 'సిద్ధార్థి', 'రౌద్ర', 'దుర్మతి', 'దుందుభి', 'రుధిరోద్గారి', 'రక్తాక్షి', 'క్రోధన', 'అక్షయ'
];

// Sanskrit 60-year cycle names (Shashtiabda Samvatsaras)
const sanskritYearNames = [
  'प्रभव', 'विभव', 'शुक्ल', 'प्रमोदूत', 'प्रजोत्पत्ति', 'आङ्गीरस', 'श्रीमुख', 'भाव', 'युव', 'धात',
  'ईश्वर', 'बहुधान्य', 'प्रमाथि', 'विक्रम', 'विषु', 'चित्रभानु', 'स्वभानु', 'तारण', 'पार्थिव', 'व्यय',
  'सर्वजित्', 'सर्वधारि', 'विरोधि', 'विकृति', 'खर', 'नन्दन', 'विजय', 'जय', 'मन्मथ', 'दुर्मुखि',
  'हेवलम्बि', 'विलम्बि', 'विकारि', 'शार्वरि', 'प्लव', 'शुभकृत्', 'शोभकृत्', 'क्रोधि', 'विश्वावसु', 'पराभव',
  'प्लवङ्ग', 'कीलक', 'सौम्य', 'साधारण', 'विरोधकृत्', 'परिधावि', 'प्रमादिच', 'आनन्द', 'राक्षस', 'अनल',
  'पिङ्गळ', 'कालयुक्ति', 'सिद्धार्थि', 'रौद्र', 'दुर्मति', 'दुन्दुभि', 'रुधिरोद्गारि', 'रक्ताक्षि', 'क्रोधन', 'अक्षय'
];

// Bilingual header text
const headerText = {
  telugu: 'నామ సంవత్సర వార్షిక పరీక్షల దరఖాస్తు పత్రము',
  sanskrit: 'नाम संवत्सर वार्षिक परीक्षा आवेदनपत्रम्'
};

const salutationText = {
  telugu: 'నమామ వేదమాతరమ్ ||',
  sanskrit: 'नमाम वेदमातरम् ||'
};
const parikshaHeading = {
  telugu: 'వేద శాస్త్ర సంవర్ధినీసభా',
  sanskrit: 'वेद शास्त्र संवर्धिनीसभा'
};

const footerText = {
  telugu: `శ్రీ వేంకటేశ్వర విద్యారణ్య వేదవిద్యాలయము,
శ్రీ విద్యారణ్య వైదిక విద్యావిహారమ్ ట్రస్ట్  (రిజిస్టర్డ్ 344/2017)
ఇంటి నెం. 4-131/1/2, హరిదాసపల్లి రోడ్, శ్రీ స్వామినారాయణ గురుకుల్ ఎదురు సందు, గ్రా|| చీర్యాల, మం. కీసర, భాగ్యనగరం - 501301
ఈ-మెయిల్: sv3vidyalayam@gmail.com, సెల్: 7995333718 - 7995333719`,
  sanskrit: `श्री वेङ्कटेश्वर विद्यारण्य वेदविद्यालयम्,
श्री विद्यारण्य वैदिक विद्याविहारम् ट्रस्ट्  (रजिस्टर्ड् 344/2017)
इन्टि नें. 4-131/1/2, हरिदासपल्लि रोड्, श्री स्वामिनारायण गुरुकुल् एदुरु सन्दु, ग्रा|| चीर्याल, मं. कीसर, भाग्यनगरं - 501301
ई-मेल्: sv3vidyalayam@gmail.com, सेल्: 7995333718 - 7995333719`
};

export default function VatuRegistration() {
  const [searchParams] = useSearchParams();
  const { lang } = useLanguage();
  const { teluguYearTelugu, teluguYearSanskrit, loading: yearLoading } = useTeluguYear();
  const [vatuData, setVatuData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const yearName = lang === 'telugu' ? teluguYearTelugu : teluguYearSanskrit;
  
  // Fetch vatu data if vatu_id is in query params
  useEffect(() => {
    const vatuId = searchParams.get('vatu_id');
    if (vatuId) {
      const fetchVatuData = async () => {
        setLoading(true);
        try {
          const res = await api.get(`/api/vatu/${vatuId}`);
          const data = await res.json();
          if (res.ok && data.success) {
            setVatuData(data.data);
          }
        } catch (err) {
          console.error('Error fetching vatu data:', err);
        } finally {
          setLoading(false);
        }
      };
      fetchVatuData();
    }
  }, [searchParams]);
  
  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 2 }}>
      {/* Breadcrumbs */}
      <Breadcrumb
        items={[
          { label: lang === 'telugu' ? 'పరీక్ష' : 'परीक्षा', path: '/parikshaMgmt' },
          { label: lang === 'telugu' ? 'నమోదు' : 'पञ्जीकरणम्' }
        ]}
      />
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <h5 style={{ color: '#00B0F0', margin: '0 0 6px 0' }}>{salutationText[lang]}</h5>
        <h1 style={{ color: '#7030A0', margin: '6px 0' }}>{parikshaHeading[lang]}</h1>
        <h2 style={{ margin: '4px 0 0 0' }}>{yearName} {headerText[lang]}</h2>
      </Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress size={40} sx={{ color: '#7f4b2d' }} />
        </Box>
      ) : (
        <VatuForm editVatu={vatuData} />
      )}
      <Box sx={{ textAlign: 'center', mt: 4, pt: 3, borderTop: '3px solid', borderColor: 'grey.300' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {lang === 'telugu' ? 'శ్రీ వేంకటేశ్వర విద్యారణ్య వేదవిద్యాలయము,' : 'श्री वेङ्कटेश्वर विद्यारण्य वेदविद्यालयम्,'}
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}>
          {lang === 'telugu' 
            ? `శ్రీ విద్యారణ్య వైదిక విద్యావిహారమ్ ట్రస్ట్  (రిజిస్టర్డ్ 344/2017)
ఇంటి నెం. 4-131/1/2, హరిదాసపల్లి రోడ్, శ్రీ స్వామినారాయణ గురుకుల్ ఎదురు సందు, గ్రా|| చీర్యాల, మం. కీసర, భాగ్యనగరం - 501301
ఈ-మెయిల్: sv3vidyalayam@gmail.com, సెల్: 7995333718 - 7995333719`
            : `श्री विद्यारण्य वैदिक विद्याविहारम् ट्रस्ट्  (रजिस्टर्ड् 344/2017)
इन्टि नें. 4-131/1/2, हरिदासपल्लि रोड्, श्री स्वामिनारायण गुरुकुल् एदुरु सन्दु, ग्रा|| चीर्याल, मं. कीसर, भाग्यनगरं - 501301
ई-मेल्: sv3vidyalayam@gmail.com, सेल्: 7995333718 - 7995333719`
          }
        </Typography>
      </Box>
    </Box>
  );
}
